import { Button, Modal, Spin } from 'antd';
import {
  CalendarOutlined,
  CarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CreditCardOutlined,
  FieldTimeOutlined,
  PlusOutlined,
  SafetyOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { checkPermissions } from '@comps/commonFunctions';
import { makeRequest } from '@utils/helpers';
import CreateLeaveRequest from '@mods/leaveManagement/leave/myLeaves/components/CreateLeaveRequest';
import CreateRelaxationRequest from '@mods/leaveManagement/relaxation/myRelaxation/components/CreateRelaxationRequest';
import CreateMyOfficalDutyReq from '@mods/travelManagement/officialDuty/myOfficialDuty/components/CreateMyOfficalDutyReq';
import CreateOvertimeRequest from '@mods/attendanceManagement/overtimes/companyOvertime/components/CreateOvertimeRequest';
import CreateCompanyAttendanceRequest from '@mods/attendanceManagement/attendances/companyAttendances/components/CreateCompanyAttendanceRequest';
import CreateMyExpense from '@mods/expenseManagement/myExpenses/components/CreateMyExpense';
import CreateCompanyLend from '@mods/lendManagement/lends/companyLends/components/CreateCompanyLend';
import CreateCompanyTravelReq from '@mods/travelManagement/travel/companyTravelRequests/components/CreateCompanyTravelReq';
import { getAllDataReq as getExpenseDataReq } from '@mods/expenseManagement/myExpenses/requests';
import { getAllDataReq as getLendDataReq } from '@mods/lendManagement/lends/companyLends/requests';
import { getAllDataReq as getTravelDataReq } from '@mods/travelManagement/travel/companyTravelRequests/requests';
import './my-space-request-launcher.css';

const MySpaceRequestLauncher = ({ modules = [] }) => {
  const authUser = useSelector((state) => state.auth.authUser);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requestForm, setRequestForm] = useState(null);
  const filters = `employee_|${authUser?.employment_id}`;
  const closeForm = () => setRequestForm(null);
  const shared = { authUser, onCompleted: closeForm, onSuccess: () => {} };
  const moduleKeys = collectModuleKeys(modules);
  const canAccess = (moduleKey) => moduleKeys.has(normalizeKey(moduleKey)) || !!checkPermissions(moduleKey);

  const requests = [
    {
      key: 'leave',
      label: 'Leave Request',
      hint: 'Annual · Sick · Casual',
      icon: <CalendarOutlined />,
      allowed: canAccess('leaveRequest'),
      open: () => setRequestForm(<CreateLeaveRequest { ...shared } lavesOf="my" filters={ filters } />),
    },
    {
      key: 'relaxation',
      label: 'Relaxation Request',
      hint: 'Short break · rest period',
      icon: <SafetyOutlined />,
      allowed: canAccess('relaxationRequests'),
      open: () => setRequestForm(<CreateRelaxationRequest { ...shared } relaxationof="my" />),
    },
    {
      key: 'expense',
      label: 'Expense Request',
      hint: 'Upload receipt',
      icon: <CreditCardOutlined />,
      allowed: canAccess('expenseRequests'),
      load: getExpenseDataReq,
      render: (data) => <CreateMyExpense { ...shared } expenses_of="my" record={ data } page={ 1 } />,
    },
    {
      key: 'loan',
      label: 'Loan Advance Request',
      hint: 'Salary advance',
      icon: <WalletOutlined />,
      allowed: canAccess('lendRequests'),
      load: getLendDataReq,
      render: (data) => <CreateCompanyLend { ...shared } lends_of="my" allData={ data } source="my-requests" filters={ filters } />,
    },
    {
      key: 'official-duty',
      label: 'Official Duty Request',
      hint: 'On-duty · field work',
      icon: <CarOutlined />,
      allowed: canAccess('officialDutyRequests'),
      open: () => setRequestForm(<CreateMyOfficalDutyReq { ...shared } />),
    },
    {
      key: 'travel',
      label: 'Travel Request',
      hint: 'Business trip',
      icon: <FieldTimeOutlined />,
      allowed: canAccess('travelRequests'),
      load: getTravelDataReq,
      render: (data) => <CreateCompanyTravelReq { ...shared } travel_types={ data.travel_types } travel_modes={ data.travel_modes } stay_types={ data.stay_types } module="my-requests" filters={ filters } />,
    },
    {
      key: 'overtime',
      label: 'Overtime Request',
      hint: 'Extra hours',
      icon: <ClockCircleOutlined />,
      allowed: canAccess('overtimeRequests'),
      open: () => setRequestForm(<CreateOvertimeRequest { ...shared } overtimeof="my" source="my-overtime" filters={ filters } />),
    },
    {
      key: 'attendance',
      label: 'Attendance Request',
      hint: 'Fix missed punch',
      icon: <CheckCircleOutlined />,
      allowed: canAccess('attendanceRequests'),
      open: () => setRequestForm(
        <CreateCompanyAttendanceRequest
          { ...shared }
          attendanceof="my"
          source="my-attendance"
          filters={ filters }
          record={ { employment_id: authUser?.employment_id } }
        />
      ),
    },
  ].filter((item) => item.allowed);

  const selectRequest = (request) => {
    setOpen(false);
    if (request.open) {
      request.open();
      return;
    }

    setLoading(true);
    makeRequest(
      setLoading,
      request.load,
      { page: 1, filters },
      (data) => setRequestForm(request.render(data)),
      () => setOpen(true)
    );
  };

  return (
    <>
      <Button className="my-space-request__trigger" type="primary" icon={ <PlusOutlined /> } onClick={ () => setOpen(true) }>
        New Request
      </Button>

      <Modal
        title={ null }
        footer={ null }
        open={ open }
        onCancel={ () => setOpen(false) }
        width={ 620 }
        centered
        className="my-space-request__modal"
      >
        <div className="my-space-request__heading">
          <h2>Start a request</h2>
          <p>Pick what you’d like to submit</p>
        </div>
        <div className="my-space-request__grid">
          { requests.map((request) => (
            <button type="button" key={ request.key } className="my-space-request__option" onClick={ () => selectRequest(request) }>
              <span className="my-space-request__icon">{ request.icon }</span>
              <span>
                <strong>{ request.label }</strong>
                <small>{ request.hint }</small>
              </span>
              <span aria-hidden="true">›</span>
            </button>
          )) }
          { requests.length === 0 ? (
            <div className="my-space-request__empty">No request types are available for your role.</div>
          ) : null }
        </div>
      </Modal>

      { loading ? <div className="my-space-request__loading"><Spin tip="Preparing request…" /></div> : null }
      { requestForm }
    </>
  );
};

export default MySpaceRequestLauncher;

const normalizeKey = (value = '') => String(value).replace(/[_\s./|-]/g, '').toLowerCase();

const collectModuleKeys = (modules, result = new Set()) => {
  (modules || []).forEach((module) => {
    result.add(normalizeKey(module.module_key || module.name));
    collectModuleKeys(module.children, result);
  });
  return result;
};
